import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { PriceCalculator } from "@finway-group/shared/lib/utils/price.calculator.utils"
import { FormInstance } from "antd"
import { useWatch } from "antd/es/form/Form"
import { useCallback, useMemo, useState } from "react"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { doAllElementsHaveTheSameFieldValue } from "Shared/utils/helper.utils"

import { FormValue, ILineItem } from "../types/LineItems.interfaces"
import { PurchaseOrderFormValues } from "../types/PurchaseOrder.interfaces"
import { resolveTax } from "../types/utils/helper.util"

interface Totals {
    totalNetPrice: number
    totalTaxPrice: number
    totalGrossPrice: number
}

export const useLineItems = (form: FormInstance<PurchaseOrderFormValues>, initialLineItems: Array<ILineItem> = []) => {
    const [expandedItems, setExpandedItems] = useState<Array<boolean>>(() => {
        if (initialLineItems.length > 0) {
            return initialLineItems.map(() => false)
        }
        return [false]
    })
    const lineItems = useWatch("lineItems", form) || []
    const currency = useWatch("currency", form) || CurrencyEnum.EUR
    const priceCalculator = useMemo(() => new PriceCalculator(currency), [currency])
    // Function to check for form validation errors in line item fields
    const checkLineItemErrors = useCallback(() => {
        try {
            const fieldsError = form.getFieldsError()
            const lineItemErrors: Array<boolean> = []

            // Check each line item for errors
            lineItems.forEach((_, index) => {
                const hasError = fieldsError.some(
                    (error) => error.name && Array.isArray(error.name) && error.name[0] === "lineItems" && error.name[1] === index && error.errors && error.errors.length > 0,
                )
                lineItemErrors[index] = hasError
            })

            return lineItemErrors
        } catch (error) {
            // Form not ready yet, return empty array
            return []
        }
    }, [form, lineItems])

    // Function to manually trigger error checking and expansion
    const checkAndExpandErrors = useCallback(() => {
        const lineItemErrors = checkLineItemErrors()

        if (lineItemErrors.length > 0) {
            setExpandedItems((prev) => prev.map((val, index) => (lineItemErrors[index] ? true : val)))
        }
    }, [checkLineItemErrors])

    // Add a check to ensure form is ready before accessing it
    const getFormLineItems = () => {
        try {
            return form?.getFieldValue("lineItems") || []
        } catch (error) {
            // Form not ready yet, return empty array
            return []
        }
    }

    const taxValues = useTaxes()

    // Calculate totals from line items in the form
    const totals = useMemo((): Totals => {
        const lineItems = getFormLineItems()
        let totalNet = 0
        let totalVat = 0
        let totalGross = 0

        lineItems.forEach((item: ILineItem) => {
            totalNet += item.totalNetPrice || 0
            totalVat += item.totalTaxPrice || 0
            totalGross += item.totalGrossPrice || 0
        })

        return {
            totalNetPrice: totalNet,
            totalTaxPrice: totalVat,
            totalGrossPrice: totalGross,
        }
    }, [form])

    const addLineItem = useCallback(() => {
        setExpandedItems((prev) => [...prev, false])
    }, [form])

    const removeLineItem = useCallback(
        (idx: number) => {
            const currentLineItems = getFormLineItems()
            if (currentLineItems.length > 1) {
                const updatedLineItems = currentLineItems.filter((_: ILineItem, i: number) => i !== idx)
                form.setFieldValue("lineItems", updatedLineItems)
                setExpandedItems((prev) => prev.filter((_, i) => i !== idx))
                updateFormTotals()
            }
        },
        [form],
    )

    const toggleExpand = useCallback((idx: number) => {
        setExpandedItems((prev) => prev.map((val, i) => (i === idx ? !val : val)))
    }, [])

    const handleUnitPriceChange = (idx: number) => {
        const currentLineItems = getFormLineItems()
        const currentItem = currentLineItems[idx]
        const taxRate = taxValues.find((tax) => tax?._id === currentItem.taxRate?._id)?.taxRate || 0

        const { totalNetPrice, totalTaxPrice, totalGrossPrice } = priceCalculator.totalsFromUnitPriceAndQuantity({
            unitPrice: currentItem.unitPrice,
            quantity: currentItem.quantity,
            taxRate,
        })

        const updateLineItems = currentLineItems.map((item: ILineItem, i: number) => {
            if (i === idx) {
                return {
                    ...item,
                    totalNetPrice,
                    totalTaxPrice,
                    totalGrossPrice,
                    taxRate: { _id: currentItem.taxRate?._id, taxRate },
                }
            }
            return item
        })

        form.setFieldValue("taxRate", resolveTax(updateLineItems))
        form.setFieldValue("lineItems", updateLineItems)
    }

    const handleTotalNetPriceChange = (idx: number) => {
        const currentLineItems = getFormLineItems()
        const currentItem = currentLineItems[idx]
        const taxRate = taxValues.find((tax) => tax._id === currentItem.taxRate._id)?.taxRate || 0

        const { totalNetPrice, totalTaxPrice, totalGrossPrice } = priceCalculator.totalsFromNetPrice({
            totalNetPrice: currentItem.totalNetPrice,
            taxRate,
        })
        const { unitPrice } = priceCalculator.unitPriceFromNetPriceAndQuantity({
            totalNetPrice,
            quantity: currentItem.quantity,
        })

        const updateLineItems = currentLineItems.map((item: ILineItem, i: number) => {
            if (i === idx) {
                return {
                    ...item,
                    unitPrice,
                    totalTaxPrice,
                    totalGrossPrice,
                }
            }
            return item
        })

        form.setFieldValue("lineItems", updateLineItems)
    }

    const handleTotalTaxPriceChange = (idx: number) => {
        const currentLineItems = getFormLineItems()
        const currentItem = currentLineItems[idx]

        const { totalNetPrice, totalGrossPrice } = priceCalculator.totalsFromNetPriceAndTaxPrice({
            totalNetPrice: currentItem.totalNetPrice,
            totalTaxPrice: currentItem.totalTaxPrice,
        })
        const { unitPrice } = priceCalculator.unitPriceFromNetPriceAndQuantity({
            totalNetPrice: currentItem.totalNetPrice,
            quantity: currentItem.quantity,
        })

        const updateLineItems = currentLineItems.map((item: ILineItem, i: number) => {
            if (i === idx) {
                return { ...item, totalGrossPrice, totalNetPrice, unitPrice }
            }
            return item
        })

        form.setFieldValue("lineItems", updateLineItems)
    }

    const handleTotalGrossPriceChange = (idx: number) => {
        const currentLineItems = getFormLineItems()
        const currentItem = currentLineItems[idx]
        const taxRate = taxValues.find((tax) => tax._id === currentItem.taxRate._id)?.taxRate || 0

        const priceCalculator = new PriceCalculator(currency)
        const { totalNetPrice, totalTaxPrice } = priceCalculator.totalsFromGrossPrice({
            totalGrossPrice: currentItem.totalGrossPrice,
            taxRate,
        })
        const { unitPrice } = priceCalculator.unitPriceFromNetPriceAndQuantity({
            totalNetPrice,
            quantity: currentItem.quantity,
        })

        const updateLineItems = currentLineItems.map((item: ILineItem, i: number) => {
            if (i === idx) {
                return {
                    ...item,
                    totalNetPrice,
                    totalTaxPrice,
                    unitPrice,
                    taxRate: { _id: currentItem.taxRate?._id, taxRate },
                }
            }
            return item
        })

        form.setFieldValue("lineItems", updateLineItems)
    }

    const validateLineItemPrices = useCallback(
        (idx: number) => {
            form.validateFields([
                [`lineItems`, idx, "unitPrice"],
                [`lineItems`, idx, "totalNetPrice"],
                [`lineItems`, idx, "totalTaxPrice"],
                [`lineItems`, idx, "totalGrossPrice"],
                ["taxRate"],
                ["totalNetPrice"],
                ["totalTaxPrice"],
                ["totalGrossPrice"],
            ]).catch(() => {
                // ignore this will update the UI validation messages
            })
        },
        [form],
    )

    const fieldsToSync: Array<keyof PurchaseOrderFormValues["lineItems"][number]> = ["unitPrice", "quantity", "totalNetPrice", "totalTaxPrice", "totalGrossPrice", "taxRate"]

    const handleCostCenterChange = () => {
        const currentLineItems = getFormLineItems()
        const isCostCenterUniform = doAllElementsHaveTheSameFieldValue(currentLineItems, "costCenter")
        if (isCostCenterUniform) {
            form.setFieldValue("costCenter", currentLineItems[0].costCenter)
        } else if (form.getFieldValue("costCenter")) {
            form.setFieldValue("costCenter", undefined)
        }
        form.validateFields(["costCenter"]).catch(() => {
            // ignore this will update the UI validation messages
        })
    }

    const handleCostCenter2Change = () => {
        const currentLineItems = getFormLineItems()
        const isCostCenter2Uniform = doAllElementsHaveTheSameFieldValue(currentLineItems, "costCenter2")
        if (isCostCenter2Uniform) {
            form.setFieldValue("costCenter2", currentLineItems[0].costCenter2)
        } else if (form.getFieldValue("costCenter2")) {
            form.setFieldValue("costCenter2", undefined)
        }
        form.validateFields(["costCenter2"]).catch(() => {
            // ignore this will update the UI validation messages
        })
    }

    const handleExpenseAccountChange = () => {
        const currentLineItems = getFormLineItems()
        const isExpenseAccountUniform = doAllElementsHaveTheSameFieldValue(currentLineItems, "expenseAccount")
        if (isExpenseAccountUniform) {
            form.setFieldValue("expenseAccount", currentLineItems[0].expenseAccount)
        } else if (form.getFieldValue("expenseAccount")) {
            form.setFieldValue("expenseAccount", undefined)
        }
        form.validateFields(["expenseAccount"]).catch(() => {
            // ignore this will update the UI validation messages
        })
    }

    const updateLineItems = (field: (typeof fieldsToSync)[number], idx: number) => {
        switch (field) {
            case "unitPrice":
            case "quantity":
            case "taxRate":
                handleUnitPriceChange(idx)
                break
            case "totalNetPrice":
                handleTotalNetPriceChange(idx)
                break
            case "totalTaxPrice":
                handleTotalTaxPriceChange(idx)
                break
            case "totalGrossPrice":
                handleTotalGrossPriceChange(idx)
                break
            case "costCenter":
                handleCostCenterChange()
                break
            case "costCenter2":
                handleCostCenter2Change()
                break
            case "expenseAccount":
                handleExpenseAccountChange()
                break
            default:
                break
        }
    }

    const updateLineItem = useCallback(
        (idx: number, _: FormValue, field: keyof ILineItem) => {
            updateLineItems(field, idx)

            if (fieldsToSync.includes(field)) {
                updateFormTotals()
                validateLineItemPrices(idx)
            }
        },
        [form],
    )

    const validateLineItems = useCallback(
        (updatedLineItems: Array<ILineItem>, field: keyof ILineItem) => {
            const validationsPaths = updatedLineItems.map((_item: ILineItem, idx: number) => [`lineItems`, idx, field])
            form.validateFields(validationsPaths).catch(() => {
                // ignore this will update the UI validation messages
            })
        },
        [form],
    )

    const updateAllLineItems = useCallback(
        (field: keyof PurchaseOrderFormValues["lineItems"][number], value: string) => {
            const currentLineItems = getFormLineItems()
            const updatedLineItems = currentLineItems.map((item: ILineItem) => ({ ...item, [field]: value }))
            form.setFieldValue("lineItems", updatedLineItems)

            validateLineItems(updatedLineItems, field)
        },
        [form],
    )

    const setLineItems = useCallback(
        (items: Array<Partial<ILineItem>>) => {
            form.setFieldValue("lineItems", items)
            setExpandedItems(items.map(() => true))
            updateFormTotals()
        },
        [form],
    )

    const updateFormTotals = useCallback(() => {
        const lineItems = getFormLineItems()

        const totalNetPrice = lineItems.reduce((acc: number, item: ILineItem) => acc + item.totalNetPrice, 0)
        const totalTaxPrice = lineItems.reduce((acc: number, item: ILineItem) => acc + item.totalTaxPrice, 0)
        const totalGrossPrice = lineItems.reduce((acc: number, item: ILineItem) => acc + item.totalGrossPrice, 0)

        const taxRate = resolveTax(lineItems)

        form.setFieldValue("taxRate", taxRate)
        form.setFieldValue("totalNetPrice", totalNetPrice)
        form.setFieldValue("totalTaxPrice", totalTaxPrice)
        form.setFieldValue("totalGrossPrice", totalGrossPrice)
    }, [form])

    const isCostCenterUniform = doAllElementsHaveTheSameFieldValue(lineItems, "costCenter")
    const isCostCenter2Uniform = doAllElementsHaveTheSameFieldValue(lineItems, "costCenter2")
    const isExpenseAccountUniform = doAllElementsHaveTheSameFieldValue(lineItems, "expenseAccount")

    return {
        lineItems,
        expandedItems,
        totals,
        addLineItem,
        removeLineItem,
        toggleExpand,
        updateLineItem,
        updateAllLineItems,
        setLineItems,
        setExpandedItems,
        isCostCenterUniform,
        isCostCenter2Uniform,
        isExpenseAccountUniform,
        checkAndExpandErrors,
    }
}
